// O QUE CADA CONVIDADO PODE FAZER NO PERFIL: por PÁGINA desde 03/11/2026 (antes, por módulo).
//
// Até 23/09 o convite tinha níveis por módulo; depois virou um privilégio único ("a equipe vê
// tudo"); em 03/10 voltou a ser por módulo (Sem acesso, Ver, Editar). Em 03/11/2026 o titular passa
// a escolher página por página ONDE OS DADOS SÃO SEPARADOS: Vendas, Perfil, Produção, Cronograma,
// Projetos, Playlists, Branding e Material. O Desenvolvimento (as três páginas leem o mesmo registro),
// a Agenda e os Arquivos continuam um acesso só cada. Separar uma página que divide os dados com
// outra só esconderia a tela, e os dados continuariam chegando pela API: por isso não se separa.
//
// No banco o acesso mora em `artist_members.access_levels` (jsonb, lista de textos):
//   'full'           → Editar em tudo;
//   '<chave>'        → Editar (a chave da página, ou a chave antiga do módulo);
//   '<chave>:ver'    → Ver;
//   ausente          → Sem acesso.
// As chaves antigas continuam valendo para as páginas que cobriam ('crm' é Vendas e Perfil,
// 'shows' é Produção, 'catalog' é Projetos e Playlists, 'marketing' é Branding e Material): quem
// já estava na equipe não muda nada. As funções `chaves_que_editam` e `chaves_que_leem` do banco
// (migração 20261103120000) dizem a MESMA regra: mudou aqui, muda lá.
//
// O Início e o aviso de pendências abrem para todos (são resumo); a Equipe continua só do titular.

import type { NivelDoConvite } from '../interfaces/maestra';

/** As áreas de dados que as telas e a RLS conhecem desde 03/10 (o "módulo" do acesso). */
export type ModuloDoPerfil = 'plan' | 'crm' | 'shows' | 'catalog' | 'agenda' | 'arquivos' | 'marketing';
export type AcessoAoModulo = 'editar' | 'ver' | 'nenhum';

/** As páginas que o titular escolhe uma a uma. */
export type PaginaDoAcesso =
  | 'plan' | 'vendas' | 'perfil' | 'producao' | 'cronograma' | 'projetos' | 'playlists' | 'lancamentos' | 'branding'
  | 'material' | 'agenda' | 'arquivos';

export interface PaginaNoAcesso {
  pagina: PaginaDoAcesso;
  rotulo: string;
  apoio: string;
  /** Só para ler: a página não tem Editar (o Cronograma). */
  soVer?: boolean;
}

export interface GrupoDoAcesso {
  chave: string;
  rotulo: string;
  paginas: PaginaNoAcesso[];
}

/** O acesso na ordem do menu: os grupos e as páginas de cada um. Página nova entra aqui e no banco. */
export const GRUPOS_DO_ACESSO: GrupoDoAcesso[] = [
  { chave: 'desenvolvimento', rotulo: 'Desenvolvimento', paginas: [
    { pagina: 'plan', rotulo: 'Desenvolvimento', apoio: 'Diagnóstico REAL, planejamento e plano de ação: as três dividem os mesmos dados' },
  ] },
  { chave: 'controle-de-shows', rotulo: 'Controle de shows', paginas: [
    { pagina: 'vendas', rotulo: 'Vendas', apoio: 'Negócios, clientes, produtos e contratos' },
    { pagina: 'producao', rotulo: 'Produção', apoio: 'Os shows confirmados, a logística, a equipe e as formações' },
    { pagina: 'cronograma', rotulo: 'Cronograma', apoio: 'O cronograma dos shows em que a pessoa está escalada', soVer: true },
  ] },
  { chave: 'perfil', rotulo: 'Perfil', paginas: [
    { pagina: 'perfil', rotulo: 'Perfil', apoio: 'A página pública do artista e o "Contrate meu show"' },
  ] },
  { chave: 'marketing', rotulo: 'Marketing', paginas: [
    { pagina: 'branding', rotulo: 'Branding', apoio: 'A identidade, o público, as fotos e o documento' },
    { pagina: 'material', rotulo: 'Material de apresentação', apoio: 'As peças, o press kit e o material de venda' },
  ] },
  { chave: 'musicas', rotulo: 'Músicas', paginas: [
    { pagina: 'projetos', rotulo: 'Projetos', apoio: 'As músicas, as versões e os arquivos das músicas' },
    { pagina: 'playlists', rotulo: 'Playlists', apoio: 'As playlists de guias e os links para ouvir' },
  ] },
  // Os Lançamentos (03/11/2026): página própria, logo depois das Músicas.
  { chave: 'lancamentos', rotulo: 'Lançamentos', paginas: [
    { pagina: 'lancamentos', rotulo: 'Lançamentos', apoio: 'Os lançamentos, as tarefas e o orçamento de cada um' },
  ] },
  { chave: 'agenda', rotulo: 'Agenda', paginas: [
    { pagina: 'agenda', rotulo: 'Agenda', apoio: 'Os compromissos do perfil' },
  ] },
  { chave: 'arquivos', rotulo: 'Arquivos', paginas: [
    { pagina: 'arquivos', rotulo: 'Arquivos', apoio: 'Os arquivos guardados no perfil' },
  ] },
];

export const PAGINAS_DO_ACESSO: PaginaDoAcesso[] = GRUPOS_DO_ACESSO.flatMap((g) => g.paginas.map((p) => p.pagina));
const DA_PAGINA = new Map(GRUPOS_DO_ACESSO.flatMap((g) => g.paginas.map((p) => [p.pagina, p] as const)));

export const ROTULO_DO_ACESSO: Record<AcessoAoModulo, string> = {
  editar: 'Editar',
  ver: 'Ver',
  nenhum: 'Sem acesso',
};

/** A chave antiga (por módulo) que também vale para a página. */
const CHAVE_ANTIGA: Partial<Record<PaginaDoAcesso, ModuloDoPerfil>> = {
  vendas: 'crm', perfil: 'crm', producao: 'shows', cronograma: 'shows',
  projetos: 'catalog', playlists: 'catalog', lancamentos: 'catalog', branding: 'marketing', material: 'marketing',
};

const ORDEM: Record<AcessoAoModulo, number> = { nenhum: 0, ver: 1, editar: 2 };
const maior = (a: AcessoAoModulo, b: AcessoAoModulo): AcessoAoModulo => (ORDEM[a] >= ORDEM[b] ? a : b);

const pelaChave = (lista: readonly string[], chave: string): AcessoAoModulo => {
  if (lista.includes(chave)) return 'editar';
  if (lista.includes(`${chave}:ver`)) return 'ver';
  return 'nenhum';
};

/** O acesso do membro a uma página: 'full', a chave da página ou a antiga do módulo. */
export const acessoAPagina = (niveis: readonly string[] | null | undefined, pagina: PaginaDoAcesso): AcessoAoModulo => {
  const lista = niveis ?? [];
  if (lista.includes('full')) return pagina === 'cronograma' ? 'ver' : 'editar';
  const antiga = CHAVE_ANTIGA[pagina];
  let acesso = maior(pelaChave(lista, pagina), antiga ? pelaChave(lista, antiga) : 'nenhum');
  if (pagina === 'cronograma') {
    // Só para ler; e quem tem a Produção o vê.
    if (acesso === 'editar') acesso = 'ver';
    if (acesso === 'nenhum' && acessoAPagina(lista, 'producao') !== 'nenhum') acesso = 'ver';
  }
  return acesso;
};

/** O acesso a todas as páginas de uma vez. */
export const acessosPorPagina = (niveis: readonly string[] | null | undefined): Record<PaginaDoAcesso, AcessoAoModulo> =>
  Object.fromEntries(PAGINAS_DO_ACESSO.map((p) => [p, acessoAPagina(niveis, p)])) as Record<PaginaDoAcesso, AcessoAoModulo>;

/**
 * O acesso à ÁREA DE DADOS (o "módulo" que as telas e a RLS de 03/10 leem). Mesma regra de
 * `acesso_do_membro` no banco: a página que edita a área, e as que só leem dela.
 */
export const acessoAoModulo = (niveis: readonly string[] | null | undefined, modulo: ModuloDoPerfil): AcessoAoModulo => {
  const p = (pagina: PaginaDoAcesso) => acessoAPagina(niveis, pagina);
  const soLe = (a: AcessoAoModulo): AcessoAoModulo => (a === 'nenhum' ? 'nenhum' : 'ver');
  switch (modulo) {
    case 'crm': return p('vendas');
    case 'shows': return p('producao');
    // Quem monta playlists (ou lançamentos) lê as músicas para escolher as faixas.
    case 'catalog': return maior(p('projetos'), maior(soLe(p('playlists')), soLe(p('lancamentos'))));
    // O Marketing de antes (as redes): edita com a chave antiga; qualquer página dele lê.
    case 'marketing': {
      const lista = niveis ?? [];
      const antiga: AcessoAoModulo = lista.includes('full') ? 'editar' : pelaChave(lista, 'marketing');
      return maior(antiga, maior(soLe(p('branding')), soLe(p('material'))));
    }
    default: return p(modulo);
  }
};

/** O acesso a todos os módulos de uma vez. */
export const MODULOS_DO_PERFIL: ModuloDoPerfil[] = ['plan', 'crm', 'shows', 'catalog', 'agenda', 'arquivos', 'marketing'];
export const acessosDoMembro = (niveis: readonly string[] | null | undefined): Record<ModuloDoPerfil, AcessoAoModulo> =>
  Object.fromEntries(MODULOS_DO_PERFIL.map((m) => [m, acessoAoModulo(niveis, m)])) as Record<ModuloDoPerfil, AcessoAoModulo>;

export type EscolhasDeAcesso = Record<PaginaDoAcesso, AcessoAoModulo>;

/**
 * Os níveis que o convite grava, a partir das escolhas da tela. Editar em tudo vira `full` (o mesmo
 * dos membros de antes); qualquer outra combinação vira a lista das páginas. O Cronograma só se
 * grava quando a Produção não o dá.
 */
export const niveisDoAcesso = (escolhas: Partial<EscolhasDeAcesso>): NivelDoConvite[] => {
  const de = (p: PaginaDoAcesso): AcessoAoModulo => escolhas[p] ?? (DA_PAGINA.get(p)?.soVer ? 'ver' : 'editar');
  const tudo = PAGINAS_DO_ACESSO.every((p) => (DA_PAGINA.get(p)?.soVer ? de(p) === 'ver' : de(p) === 'editar'));
  if (tudo) return ['full'];
  return PAGINAS_DO_ACESSO.flatMap((p): NivelDoConvite[] => {
    const a = de(p);
    if (p === 'cronograma') return a !== 'nenhum' && de('producao') === 'nenhum' ? ['cronograma:ver'] : [];
    return a === 'editar' ? [p] : a === 'ver' ? [`${p}:ver`] : [];
  });
};

/** Tudo em Editar (o Cronograma, que é só para ler, em Ver): o convite novo começa daqui. */
export const TUDO_EDITAR = (): EscolhasDeAcesso => acessosPorPagina(['full']);

// ─── OS MODELOS PRONTOS (03/11/2026) ───────────────────────────────────────────────────────────
//
// O titular escolhe um modelo e, se quiser, personaliza página por página. Um modelo é só um ponto
// de partida: o que se grava são as páginas (`niveisDoAcesso`).

export type ModeloDeAcesso = 'tudo' | 'gestao' | 'producao' | 'comunicacao' | 'criacao' | 'musico' | 'so_ver';

const escolhas = (padrao: AcessoAoModulo, mais: Partial<EscolhasDeAcesso>): EscolhasDeAcesso => ({
  ...(Object.fromEntries(PAGINAS_DO_ACESSO.map((p) => [p, DA_PAGINA.get(p)?.soVer && padrao === 'editar' ? 'ver' : padrao])) as EscolhasDeAcesso),
  ...mais,
});

export const MODELOS_DE_ACESSO: { modelo: ModeloDeAcesso; rotulo: string; apoio: string; escolhas: EscolhasDeAcesso }[] = [
  { modelo: 'tudo', rotulo: 'Edita tudo', apoio: 'Empresário, sócio: o perfil inteiro, menos convidar e a cobrança', escolhas: TUDO_EDITAR() },
  { modelo: 'gestao', rotulo: 'Gestão', apoio: 'Vendas, Produção, Agenda e Arquivos; vê o resto', escolhas: escolhas('ver', {
    vendas: 'editar', producao: 'editar', perfil: 'editar', agenda: 'editar', arquivos: 'editar' }) },
  { modelo: 'producao', rotulo: 'Produção', apoio: 'Produção, Agenda e Arquivos; vê as músicas', escolhas: escolhas('nenhum', {
    producao: 'editar', cronograma: 'ver', agenda: 'editar', arquivos: 'editar', projetos: 'ver' }) },
  { modelo: 'comunicacao', rotulo: 'Comunicação', apoio: 'Marketing, Perfil e Arquivos; vê Agenda e Músicas', escolhas: escolhas('nenhum', {
    perfil: 'editar', branding: 'editar', material: 'editar', arquivos: 'editar', agenda: 'ver', projetos: 'ver', playlists: 'ver',
    lancamentos: 'ver' }) },
  { modelo: 'criacao', rotulo: 'Criação', apoio: 'Projetos, Playlists e Lançamentos; vê a Agenda', escolhas: escolhas('nenhum', {
    projetos: 'editar', playlists: 'editar', lancamentos: 'editar', agenda: 'ver', arquivos: 'ver' }) },
  { modelo: 'musico', rotulo: 'Músico', apoio: 'O Cronograma dos shows dele, a Agenda e as Músicas, sem editar', escolhas: escolhas('nenhum', {
    cronograma: 'ver', agenda: 'ver', projetos: 'ver', playlists: 'ver' }) },
  { modelo: 'so_ver', rotulo: 'Só ver', apoio: 'Vê tudo, não muda nada', escolhas: escolhas('ver', {}) },
];

/** O modelo que as escolhas são, ou `null` quando foram personalizadas. */
export const modeloDasEscolhas = (e: EscolhasDeAcesso): ModeloDeAcesso | null => {
  // O Cronograma vem com a Produção: comparar o que vale de fato.
  const vale = (x: EscolhasDeAcesso) => acessosPorPagina(niveisDoAcesso(x));
  const atual = vale(e);
  return MODELOS_DE_ACESSO.find((m) => {
    const dele = vale(m.escolhas);
    return PAGINAS_DO_ACESSO.every((p) => dele[p] === atual[p]);
  })?.modelo ?? null;
};

/** O resumo de uma linha da Equipe: "Edita tudo", "Músico", "Edita Vendas e Produção; vê Agenda". */
export const resumoDoAcesso = (niveis: readonly string[] | null | undefined): string => {
  const acessos = acessosPorPagina(niveis);
  if ((niveis ?? []).includes('full')) return 'Edita tudo';
  const modelo = modeloDasEscolhas(acessos);
  if (modelo === 'so_ver') return 'Vê tudo';
  const de = (a: AcessoAoModulo) => PAGINAS_DO_ACESSO.filter((p) => acessos[p] === a).map((p) => DA_PAGINA.get(p)?.rotulo ?? p);
  const edita = de('editar');
  const ve = de('ver');
  if (!edita.length && !ve.length) return 'Sem acesso às páginas';
  const junta = (l: string[]) => (l.length > 1 ? `${l.slice(0, -1).join(', ')} e ${l[l.length - 1]}` : l[0]);
  return [edita.length ? `Edita ${junta(edita)}` : null, ve.length ? `vê ${junta(ve)}` : null]
    .filter(Boolean).join('; ').replace(/^v/, 'V');
};
